/* @jsx h */
import { defineComponent, h, provide, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { NavStackContextKey, type NavStackContextType } from "@lemmonhq/uikit";

type RouteNavState = {
  type: "route";
  path: string;
};

type SheetNavState = {
  type: "sheet";
  id: string;
  onBack: () => void;
};

type NavState = RouteNavState | SheetNavState;

let sheetIdCounter = 0;

/**
 * Tracks a stack of "nav states" — route changes pushed automatically, and
 * sheet/modal states pushed by components like FullScreenSheet. Used by
 * NativeBridge's handleBack to close the topmost sheet before falling back
 * to router navigation.
 */
export const NavStateProvider = defineComponent({
  name: "NavStateProvider",
  setup(_, { slots }) {
    const router = useRouter();
    const stack = ref<NavState[]>([]);

    watch(
      () => router.currentRoute.value.path,
      (path) => {
        stack.value.push({ type: "route", path });
      },
      { immediate: true },
    );

    const pushSheet = (onBack: () => void): string => {
      const id = `sheet-${++sheetIdCounter}`;
      stack.value.push({ type: "sheet", id, onBack });
      return id;
    };

    const popSheet = (id: string) => {
      stack.value = stack.value.filter(
        (s) => !(s.type === "sheet" && s.id === id),
      );
    };

    const popTop = (): boolean => {
      const top = stack.value[stack.value.length - 1];
      if (!top) return false;

      if (top.type === "sheet") {
        stack.value.pop();
        top.onBack();
        return true;
      }

      // Route entry — pop it and let the caller fall back to router.back()
      stack.value.pop();
      return false;
    };

    const navStackContext: NavStackContextType = {
      pushSheet,
      popSheet,
      popTop,
    };

    provide(NavStackContextKey, navStackContext);

    return () => <>{slots.default?.()}</>;
  },
});
